import {
  Box,
  Button,
  Flex,
  HStack,
  Heading,
  Stack,
  Table,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
  VStack,
  Spinner,
  Select,
  Input,
  useColorModeValue as mode,
  Alert,
  AlertIcon,
} from "@chakra-ui/react";
import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import ReactApexChart from "react-apexcharts";
import {
  startOfWeek,
  endOfWeek,
  startOfMonth,
  endOfMonth,
  startOfYear,
  endOfYear,
  subWeeks,
  subMonths,
  subYears,
} from "date-fns";
import PostcodeModal from "./screenComponents/PostcodeModal";

const formatCurrency = (value) =>
  new Intl.NumberFormat("en-AU", { style: "currency", currency: "AUD", maximumFractionDigits: 0 }).format(value || 0);

const formatDateInput = (date) => date.toISOString().split("T")[0];

const presets = [
  { value: "thisWeek", label: "This Week" },
  { value: "lastWeek", label: "Last Week" },
  { value: "thisMonth", label: "This Month" },
  { value: "lastMonth", label: "Last Month" },
  { value: "thisYear", label: "This Year" },
  { value: "lastYear", label: "Last Year" },
  { value: "custom", label: "Custom" },
];

const getRange = (preset) => {
  const today = new Date();
  switch (preset) {
    case "thisWeek": {
      const start = startOfWeek(today, { weekStartsOn: 1 });
      const end = endOfWeek(today, { weekStartsOn: 1 });
      return { start, end };
    }
    case "lastWeek": {
      const start = startOfWeek(subWeeks(today, 1), { weekStartsOn: 1 });
      const end = endOfWeek(subWeeks(today, 1), { weekStartsOn: 1 });
      return { start, end };
    }
    case "thisMonth": {
      return { start: startOfMonth(today), end: endOfMonth(today) };
    }
    case "lastMonth": {
      return { start: startOfMonth(subMonths(today, 1)), end: endOfMonth(subMonths(today, 1)) };
    }
    case "thisYear": {
      return { start: startOfYear(today), end: endOfYear(today) };
    }
    case "lastYear": {
      const last = subYears(today, 1);
      return { start: startOfYear(last), end: endOfYear(last) };
    }
    default: {
      return { start: startOfYear(today), end: endOfYear(today) };
    }
  }
};

const PostcodeStatsScreen = () => {
  const [preset, setPreset] = useState("thisYear");
  const initialRange = getRange("thisYear");
  const [startDate, setStartDate] = useState(initialRange.start);
  const [endDate, setEndDate] = useState(initialRange.end);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [stats, setStats] = useState([]);
  const [totalJobs, setTotalJobs] = useState(0);
  const [postcodeModalOpen, setPostcodeModalOpen] = useState(false);
  const [selectedPostcode, setSelectedPostcode] = useState("");

  const fetchStats = async (from, to) => {
    setLoading(true);
    setError(null);
    try {
      const { data } = await axios.post("/api/jobs/postcode-stats", { dateFrom: from, dateTo: to });
      setStats(data.stats || []);
      setTotalJobs(data.totalJobs || 0);
    } catch (err) {
      const msg = err.response?.data?.message || err.message || "Failed to load postcode statistics";
      setError(msg);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchStats(startDate, endDate);
  }, []);

  const handlePresetChange = (value) => {
    setPreset(value);
    if (value === "custom") return;
    const range = getRange(value);
    setStartDate(range.start);
    setEndDate(range.end);
    fetchStats(range.start, range.end);
  };

  const handleCustomChange = (field, raw) => {
    const parsed = raw ? new Date(raw) : new Date();
    if (field === "start") setStartDate(parsed);
    if (field === "end") setEndDate(parsed);
  };

  const applyCustomRange = () => {
    fetchStats(startDate, endDate);
  };

  const chartData = useMemo(() => {
    const topStats = [...stats].sort((a, b) => b.jobs - a.jobs).slice(0, 20);
    return {
      series: [
        {
          name: "Jobs",
          data: topStats.map((s) => s.jobs),
        },
      ],
      options: {
        chart: {
          type: "bar",
          toolbar: { show: true },
        },
        plotOptions: {
          bar: {
            horizontal: true,
            distributed: true,
          },
        },
        colors: ["#3182ce"],
        dataLabels: {
          enabled: false,
        },
        xaxis: {
          categories: topStats.map((s) => s.postcode),
          labels: {
            formatter: (value) => `${value}`,
          },
          title: {
            text: "Jobs",
          },
        },
        tooltip: {
          y: {
            formatter: (val) => `${val} jobs`,
          },
        },
      },
    };
  }, [stats]);

  const bgColor = mode("white", "gray.800");
  const borderColor = mode("gray.200", "gray.700");
  const headerBg = mode("gray.50", "gray.700");
  const rowHoverBg = mode("gray.100", "gray.600");

  return (
    <Box maxW='7xl' mx='auto' px={{ base: 4, md: 8 }} py={{ base: 6, md: 10 }}>
      <VStack align='stretch' spacing={8}>
        <Box>
          <Heading size='lg' mb={2}>
            Postcode Statistics
          </Heading>
          <Text color='gray.600' fontSize='sm'>
            Jobs distribution by postcode for the selected date range
          </Text>
        </Box>

        <Flex gap={4} direction={{ base: "column", md: "row" }} align={{ base: "stretch", md: "center" }}>
          <Box minW='200px'>
            <Text fontWeight='bold' mb={2} fontSize='sm'>
              Range
            </Text>
            <Select value={preset} onChange={(e) => handlePresetChange(e.target.value)}>
              {presets.map((p) => (
                <option key={p.value} value={p.value}>
                  {p.label}
                </option>
              ))}
            </Select>
          </Box>

          {preset === "custom" && (
            <HStack spacing={3} align='flex-end' flexWrap='wrap'>
              <Box>
                <Text fontWeight='bold' mb={1} fontSize='sm'>
                  Start
                </Text>
                <Input
                  type='date'
                  value={formatDateInput(startDate)}
                  onChange={(e) => handleCustomChange("start", e.target.value)}
                />
              </Box>
              <Box>
                <Text fontWeight='bold' mb={1} fontSize='sm'>
                  End
                </Text>
                <Input
                  type='date'
                  value={formatDateInput(endDate)}
                  onChange={(e) => handleCustomChange("end", e.target.value)}
                />
              </Box>
              <Button colorScheme='blue' size='sm' onClick={applyCustomRange}>
                Apply
              </Button>
            </HStack>
          )}
        </Flex>

        {error && (
          <Alert status='error' borderRadius='md'>
            <AlertIcon />
            {error}
          </Alert>
        )}

        <Box bg={bgColor} borderRadius='lg' borderWidth='1px' borderColor={borderColor} p={6} shadow='md'>
          {loading ? (
            <Flex justify='center' align='center' h='320px'>
              <Spinner size='lg' />
            </Flex>
          ) : stats.length === 0 ? (
            <Flex justify='center' align='center' h='200px'>
              <Text color='gray.500'>No jobs found for this range</Text>
            </Flex>
          ) : (
            <ReactApexChart type='bar' series={chartData.series} options={chartData.options} height={420} />
          )}
        </Box>

        <Box bg={bgColor} borderRadius='lg' borderWidth='1px' borderColor={borderColor} shadow='md' overflowX='auto'>
          <Table variant='simple' size='sm'>
            <Thead bg={headerBg}>
              <Tr>
                <Th>Postcode</Th>
                <Th isNumeric>Jobs</Th>
                <Th isNumeric>% of Total</Th>
              </Tr>
            </Thead>
            <Tbody>
              {loading ? (
                <Tr>
                  <Td colSpan={3} textAlign='center' py={6}>
                    <Spinner size='md' />
                  </Td>
                </Tr>
              ) : stats.length === 0 ? (
                <Tr>
                  <Td colSpan={3} textAlign='center' py={6}>
                    <Text color='gray.500'>No jobs found</Text>
                  </Td>
                </Tr>
              ) : (
                stats.map((row, idx) => {
                  const percent = totalJobs ? Math.round((row.jobs / totalJobs) * 1000) / 10 : 0;
                  return (
                    <Tr key={idx} _hover={{ bg: rowHoverBg }}>
                      <Td fontWeight='medium'>
                        <Button
                          variant='link'
                          colorScheme='blue'
                          size='sm'
                          onClick={() => {
                            setSelectedPostcode(row.postcode);
                            setPostcodeModalOpen(true);
                          }}
                        >
                          {row.postcode}
                        </Button>
                      </Td>
                      <Td isNumeric>{row.jobs}</Td>
                      <Td isNumeric>{percent}%</Td>
                    </Tr>
                  );
                })
              )}
            </Tbody>
          </Table>
        </Box>

        <PostcodeModal
          isOpen={postcodeModalOpen}
          onClose={() => setPostcodeModalOpen(false)}
          postcode={selectedPostcode}
        />
      </VStack>
    </Box>
  );
};

export default PostcodeStatsScreen;
